import { HotelsSearchInput } from '@shared/components/HotelsSearchInput';
import { LocationInputV2 } from '@shared/components/LocationInputV2';
import { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { withWindow } from '@shared/utils/withWindow';
import { FormControl, Box, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useField } from 'formik';
import React from 'react';
import * as Yup from 'yup';

const LOCATION_INPUT_FONT_SIZE = 13;
const StyledLocationContainer = styled(Box)`
  div[aria-haspopup='listbox'] {
    border-radius: ${pxToRem(16)};
  }

  div[aria-haspopup='listbox'] input {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)};
    text-overflow: ellipsis;
  }

  div[aria-haspopup='listbox'] input::placeholder {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)};
  }
`;

const searchInputOverrides = {
  Root: {
    props: {
      maxWidth: '100%',
    },
  },
  Button: {
    props: {
      backgroundColor: 'transparent',
      color: 'buttonOutlineNeutralText',
      _hover: {
        backgroundColor: 'transparent',
        color: 'buttonOutlineNeutralText',
      },
      _active: {
        backgroundColor: 'transparent',
        color: 'buttonOutlineNeutralText',
      },
    },
  },
};

const schema = Yup.object({
  displayName: Yup.string(),
  latitude: Yup.number(),
  longitude: Yup.number(),
  googlePlaceId: Yup.string(),
}).test('location', 'Invalid location', function (value) {
  if (!value) {
    return false;
  }
  return value.displayName && value.latitude && value.longitude;
});
type ISchema = {
  displayName: string;
  latitude: number;
  longitude: number;
  googlePlaceId: string;
};

const initialValue = {
  displayName: '',
  googlePlaceId: '',
  latitude: undefined,
  longitude: undefined,
};

const Fields = ({ fieldsId }: { fieldsId: string }) => {
  // avoid passing value because I want LocationInputV2 to be an uncontrolled component, we just want the location info
  const [{ value, ...restField }, meta, helper] = useField<Partial<ISchema>>(fieldsId);
  const isMobileOrTablet = useIsMobileOrTablet();
  const onChange = (response: LocationInputV2Response | null) => {
    if (!response) {
      return;
    }
    helper.setValue({
      displayName: response.displayName,
      latitude: response.lat,
      longitude: response.lng,
      googlePlaceId: response.placeId,
    });
    // This code is added to lose focus, especially on mobile, after searching for results.
    withWindow(() => {
      if (document.activeElement) {
        const activeElement = document.activeElement as HTMLElement;

        activeElement?.blur();
      }
    });
  };
  // meta.error is not a string, it's an object but formik doesn't realize that
  const errorMessage = (meta.touched && meta.error) || undefined;

  return (
    <FormControl
      {...meta}
      zIndex={1200}
      width="100%"
      maxWidth={{ _: 'full', md: pxToRem(260) }}
      position={{ _: 'static', md: 'absolute' }}
      top={0}
      isInvalid={!!errorMessage}
      error={errorMessage}
    >
      {isMobileOrTablet ? (
        <HotelsSearchInput
          {...restField}
          handleOnSearch={res =>
            onChange({
              placeId: res?.location?.placeId || '',
              displayName: res?.location?.detailedPlace || '',
              lat: res?.location?.lat || 0,
              lng: res?.location?.lng || 0,
            })
          }
          handleOnChangeLocation={res =>
            onChange({
              placeId: res?.location?.placeId || '',
              displayName: res?.location?.detailedPlace || '',
              lat: res?.location?.lat || 0,
              lng: res?.location?.lng || 0,
            })
          }
          hideIcon
          overrides={searchInputOverrides}
          eventLocationData={{ place: value.displayName || '', lat: value.latitude, lng: value.longitude, placeId: '' }}
        />
      ) : (
        <StyledLocationContainer>
          <LocationInputV2
            {...restField}
            includeMapIcon
            iconRight={null}
            noBorder
            placeholder="Center Park Boathouse"
            placePredictionTypes={[]}
            isHideListItemIcon={true}
            defaultValue={value.displayName}
            onLocationChange={onChange}
            includeHotelIcon={false}
          />
        </StyledLocationContainer>
      )}
    </FormControl>
  );
};

export { Fields, schema, initialValue };
